import { IconButton } from '@mui/material';
import { PropsWithChildren, useEffect, useState } from 'react';

import { SpinnerLoading } from '../styles';
import * as S from './styles';
import { isNullUndefined } from '@/main/services/utils';
import ArrowIcon from '@/main/components/svg/arrowIcon';

interface IKOGCarouselCardProps {
  cardsLenght?: number;
  isAbsoluteControls?: boolean;
  currentIndex?: number;
  totalRecords?: number;
  isLoading?: boolean;
  backToFirstPage?: boolean;
  backToLastPage?: boolean;
  onClickNextPage?: () => Promise<void> | void;
  setCurrentIndex?: React.Dispatch<React.SetStateAction<number>>;
}

function KOGCarouselCard({
  cardsLenght = 0,
  isAbsoluteControls = false,
  currentIndex: externalCurrentIndex,
  setCurrentIndex: externalSetCurrentIndex,
  totalRecords,
  isLoading = false,
  backToFirstPage = false,
  backToLastPage = false,
  onClickNextPage,
  children
}: PropsWithChildren<IKOGCarouselCardProps>) {
  const [internalCurrentIndex, setInternalCurrentIndex] = useState(0);

  const currentIndex = isNullUndefined(externalCurrentIndex)
    ? internalCurrentIndex
    : externalCurrentIndex;

  function onIndexChangeHandler(newIndex: number) {
    if (externalSetCurrentIndex) {
      externalSetCurrentIndex(newIndex);
    } else {
      setInternalCurrentIndex(newIndex);
    }
  }

  const handleNext = async () => {
    const isLastCard = currentIndex === cardsLenght - 1;
    const isShowingAllRecords = totalRecords === cardsLenght;

    if (isLastCard && totalRecords && onClickNextPage && !isShowingAllRecords) {
      await onClickNextPage();
    }

    if (currentIndex < cardsLenght - 1) {
      const newIndex = currentIndex + 1;
      onIndexChangeHandler(newIndex);
    } else if (isLastCard && backToFirstPage && isShowingAllRecords) {
      onIndexChangeHandler(0);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      onIndexChangeHandler(newIndex);
    } else if (currentIndex === 0 && backToLastPage) {
      onIndexChangeHandler(cardsLenght - 1);
    }
  };
  const isLastCard = currentIndex === cardsLenght - 1;
  const isShowingAllRecords =
    !isNullUndefined(totalRecords) && totalRecords === cardsLenght;
  const disabledNext =
    (isLastCard && isShowingAllRecords && !backToFirstPage) ||
    (isLoading && isLastCard);
  const disabledPrev = currentIndex === 0 && !backToLastPage;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && !disabledPrev) {
        handlePrev();
      } else if (event.key === 'ArrowRight' && !disabledNext) {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentIndex, cardsLenght]);

  return (
    <S.CarouselContainer>
      <S.CardCounter>
        {currentIndex + 1}/{cardsLenght}
      </S.CardCounter>
      <S.Controls $isAbsoluteControls={isAbsoluteControls}>
        <S.BaseIconModal>
          <IconButton onClick={handlePrev} disabled={disabledPrev}>
            <ArrowIcon direction="left" />
          </IconButton>
        </S.BaseIconModal>
        <S.BaseIconModal>
          <IconButton onClick={handleNext} disabled={disabledNext}>
            {isLastCard && isLoading ? (
              <SpinnerLoading />
            ) : (
              <ArrowIcon direction="right" />
            )}
          </IconButton>
        </S.BaseIconModal>
      </S.Controls>
      <S.CardWrapper $translate={currentIndex}>{children}</S.CardWrapper>
    </S.CarouselContainer>
  );
}

export default KOGCarouselCard;
